import type { Conversation } from '@/lib/messages';

interface ConversationListProps {
  conversations: Conversation[];
  activeId: string | null;
  loading: boolean;
  searchQuery: string;
  onSearchChange: (value: string) => void;
  onSelect: (conversation: Conversation) => void;
  onNewConversation: () => void;
}

function getInitials(name: string | null | undefined): string {
  if (!name) return '?';
  return name
    .split(' ')
    .filter(Boolean)
    .slice(0, 2)
    .map((n) => n[0])
    .join('')
    .toUpperCase();
}

function formatTime(iso: string | null): string {
  if (!iso) return '';
  const date = new Date(iso);
  const now = new Date();
  if (date.toDateString() === now.toDateString()) {
    return date.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' });
  }
  return date.toLocaleDateString('fr-FR', { day: '2-digit', month: 'short' });
}

function ConversationList({
  conversations,
  activeId,
  loading,
  searchQuery,
  onSearchChange,
  onSelect,
  onNewConversation,
}: ConversationListProps) {
  const filtered = conversations.filter((c) => {
    const q = searchQuery.trim().toLowerCase();
    if (!q) return true;
    const name = c.other_user?.full_name?.toLowerCase() ?? '';
    const email = c.other_user?.email?.toLowerCase() ?? '';
    return name.includes(q) || email.includes(q);
  });

  return (
    <div className="flex flex-col h-full">
      <div className="p-4 border-b border-background-200/70">
        <div className="flex items-center justify-between mb-3">
          <h2 className="font-heading font-semibold text-lg text-foreground-50">Messagerie</h2>
          <button
            onClick={onNewConversation}
            className="w-9 h-9 flex items-center justify-center rounded-lg bg-primary-500 text-foreground-950 hover:bg-primary-400 transition-colors"
            aria-label="Nouvelle conversation"
          >
            <i className="ri-edit-2-line text-lg" />
          </button>
        </div>
        <div className="flex items-center bg-background-50 border border-background-300/60 rounded-lg px-3">
          <i className="ri-search-line text-base text-foreground-500" />
          <input
            type="text"
            value={searchQuery}
            onChange={(e) => onSearchChange(e.target.value)}
            placeholder="Rechercher..."
            className="flex-1 bg-transparent text-sm text-foreground-50 placeholder:text-foreground-500 py-2 pl-2 outline-none"
          />
        </div>
      </div>

      <div className="flex-1 overflow-y-auto">
        {loading ? (
          <div className="flex items-center justify-center py-12">
            <div className="w-8 h-8 flex items-center justify-center rounded-full bg-primary-500/10">
              <i className="ri-loader-4-line text-primary-400 animate-spin" />
            </div>
          </div>
        ) : filtered.length === 0 ? (
          <div className="text-center py-12 px-4">
            <div className="w-14 h-14 mx-auto flex items-center justify-center rounded-full bg-background-100 mb-3">
              <i className="ri-chat-3-line text-xl text-foreground-400" />
            </div>
            <p className="text-sm text-foreground-400">Aucune conversation</p>
            <button
              onClick={onNewConversation}
              className="mt-3 text-sm font-semibold text-primary-400 hover:text-primary-300 whitespace-nowrap"
            >
              Démarrer une discussion
            </button>
          </div>
        ) : (
          <div className="py-2">
            {filtered.map((c) => {
              const active = c.id === activeId;
              const name = c.other_user?.full_name || c.other_user?.email || 'Utilisateur';
              return (
                <button
                  key={c.id}
                  onClick={() => onSelect(c)}
                  className={`w-full flex items-center gap-3 px-4 py-3 text-left transition-colors ${
                    active ? 'bg-primary-500/10' : 'hover:bg-background-100'
                  }`}
                >
                  <div className="relative shrink-0">
                    {c.other_user?.avatar_url ? (
                      <img
                        src={c.other_user.avatar_url}
                        alt={name}
                        className="w-11 h-11 rounded-full object-cover"
                      />
                    ) : (
                      <div className="w-11 h-11 flex items-center justify-center rounded-full bg-secondary-500/15 text-secondary-400 text-sm font-semibold">
                        {getInitials(c.other_user?.full_name)}
                      </div>
                    )}
                    {(c.unread_count ?? 0) > 0 && (
                      <span className="absolute -top-0.5 -right-0.5 min-w-[18px] h-[18px] px-1 flex items-center justify-center rounded-full bg-accent-500 text-foreground-950 text-[10px] font-bold">
                        {c.unread_count}
                      </span>
                    )}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center justify-between gap-2">
                      <span className="text-sm font-semibold text-foreground-50 truncate">{name}</span>
                      <span className="text-xs text-foreground-500 whitespace-nowrap">
                        {formatTime(c.last_message_at)}
                      </span>
                    </div>
                    <p
                      className={`text-xs truncate mt-0.5 ${
                        (c.unread_count ?? 0) > 0 ? 'text-foreground-50 font-medium' : 'text-foreground-400'
                      }`}
                    >
                      {c.last_message_preview || 'Nouvelle conversation'}
                    </p>
                  </div>
                </button>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}

export default ConversationList;